const defaultTranslate = (_, fallback = _, replacements = null) => {
  if (typeof fallback !== 'string' || !replacements || typeof replacements !== 'object') {
    return fallback;
  }

  return fallback.replace(/\{(\w+)\}/g, (_, token) => {
    const replacement = replacements[token];
    return replacement === undefined || replacement === null ? `{${token}}` : String(replacement);
  });
};

const getActionLabels = (t = defaultTranslate) => ({
  purchase: t('subscription.actions.purchase', 'Start Ultra in iOS app'),
  restore: t('subscription.actions.restore', 'Already subscribed? Restore purchase'),
  manage: t('subscription.actions.manage', 'Manage subscription'),
});

export const ULTRA_MONTHLY_PRICE_LABEL = '€9.99/month';

export const getSubscriptionPlanLabel = (status, t = defaultTranslate) =>
  status === 'ultra'
    ? t('subscription.planNames.ultra', 'Ultra')
    : t('subscription.planNames.freeTier', 'Free tier');

export const getSubscriptionRenewalLabel = (subscription, options = {}) => {
  const { locale, t = defaultTranslate } = options;
  const expiresAt = subscription?.revenueCat?.expiresAt;
  if (!expiresAt) {
    return null;
  }

  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return t('subscription.renewalLabel', 'Renews or expires on {date}', {
    date: date.toLocaleDateString(locale || undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  });
};

export const getSubscriptionPageState = ({ user, isNativeApp, caps, locale, t = defaultTranslate }) => {
  const isUltra = user?.subscription?.status === 'ultra';
  const canUseNativePurchases = Boolean(isNativeApp && caps?.subscriptions);
  return {
    isUltra,
    canUseNativePurchases,
    planLabel: getSubscriptionPlanLabel(user?.subscription?.status, t),
    renewalLabel: getSubscriptionRenewalLabel(user?.subscription, { locale, t }),
  };
};

export const getNativeSubscriptionMessageType = (action) => {
  if (action === 'purchase') return 'FORZA_SUBSCRIPTION_PURCHASE';
  if (action === 'restore') return 'FORZA_SUBSCRIPTION_RESTORE';
  if (action === 'manage') return 'FORZA_SUBSCRIPTION_MANAGE';
  return null;
};

export const getSubscriptionResultStatus = (payload, t = defaultTranslate) => {
  if (!payload || payload.type !== 'FORZA_SUBSCRIPTION_RESULT') {
    return null;
  }

  const actionLabels = getActionLabels(t);

  if (payload.status === 'pending') {
    return {
      action: payload.action,
      state: 'pending',
      message: t('subscription.status.pending', '{action} started...', {
        action: actionLabels[payload.action] || t('subscription.status.genericAction', 'Subscription action'),
      }),
    };
  }

  if (payload.status === 'success') {
    return {
      action: payload.action,
      state: 'success',
      message: payload.entitlementActive
        ? t('subscription.status.successActive', 'Ultra access is active on this account.')
        : t('subscription.status.successInactive', 'Purchase state refreshed. Ultra is not active yet.'),
    };
  }

  if (payload.status === 'cancelled') {
    return {
      action: payload.action,
      state: 'idle',
      message: t('subscription.status.cancelled', 'Purchase cancelled.'),
    };
  }

  return {
    action: payload.action,
    state: 'error',
    message: payload.error || t('subscription.status.failed', 'Subscription action failed.'),
  };
};
